/**
 * Formatage des noms de collections et catégories
 * Transforme "Canapés Et Méridiennes" en "Canapés et méridiennes"
 * Tout en préservant la majuscule pour "Hermès" et autres mots spéciaux
 */

/**
 * Parse des codes multiples depuis différents formats
 * @param input - String ou array contenant les codes
 * @returns Array des codes parsés
 */
export function parseMultipleCodes(input: string | string[]): string[] {
  if (Array.isArray(input)) {
    return input.filter(Boolean);
  }

  if (typeof input !== 'string') {
    return [];
  }

  // Format PostgreSQL array: {"M1029","M0016"}
  if (input.startsWith('{') && input.endsWith('}')) {
    return input.slice(1, -1)
      .split(',')
      .map(code => code.trim().replace(/"/g, ''))
      .filter(Boolean);
  }

  // Format JSON array: ["M1029","M0016"]
  if (input.startsWith('[') && input.endsWith(']')) {
    try {
      const parsed = JSON.parse(input);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  // Format simple avec tirets: M1029-M0016
  return input.split('-').filter(Boolean);
}


/**
 * Formate le nom d'un modèle selon les nouvelles règles métier
 * @param product - Le produit contenant les informations
 * @param catalogue - Le type de catalogue ('mobilier' ou 'porcelaine' ou 'orfevrerie')
 * @param globalStore - Store global pour accéder aux paramètres
 * @param currentCategoryName - Nom de la catégorie/collection courante (optionnel, pour sous-catégories)
 * @returns Le nom du modèle formaté selon les règles
 */
export function formatModelName(product: any, catalogue: string, globalStore: any, currentCategoryName?: string): string {
  if (!product) return '';

  const lang = globalStore?.language || 'fr';

  const parseJsonField = (field: any, l: string): string => {
    if (!field) return '';
    if (typeof field === 'string') {
      try { const o = JSON.parse(field); return o[l] || o.fr || o.en || ''; } catch { return field; }
    }
    return field[l] || field.fr || field.en || '';
  };

  if (catalogue === 'porcelaine' || catalogue === 'lth') {
    return parseJsonField(product.title, lang);
  }

  if (catalogue === 'mobilier' || catalogue === 'orfevrerie') {
    let objectTypeTitle = parseJsonField(product.objectTypeTitle, lang);

    if (!objectTypeTitle) {
      objectTypeTitle = parseJsonField(product.title, lang);
    }

    let collectionName = '';
    if (catalogue === 'mobilier') {
      if (product.codeLigne && globalStore.parameters?.data) {
        const collectionParam = globalStore.parameters.data.find((param: any) => {
          if (param.key !== 'ref_col_mobilier') return false;
          const codes = parseMultipleCodes(param.category_code);
          return codes.includes(product.codeLigne);
        });
        if (collectionParam) {
          collectionName = collectionParam[lang] || collectionParam.fr || '';
        } else {
          collectionName = product.codeLigne;
          console.warn(`[formatModelName] Aucun paramètre BO ref_col_mobilier trouvé pour codeLigne: ${product.codeLigne}.`);
        }
      } else if (currentCategoryName) {
        collectionName = currentCategoryName;
      }
    }

    return [objectTypeTitle, collectionName].filter(Boolean).join(' ');
  }

  return product.libelleModele || product.libelleProduit || parseJsonField(product.title, lang) || '';
}


/**
 * Formate le nom d'une couleur en ajoutant des espaces autour des "/"
 * @param colorName - Le nom de couleur à formater
 * @returns Le nom de couleur formaté avec des espaces autour des "/"
 */
export function formatColorName(colorName: string): string {
  if (!colorName || typeof colorName !== 'string') return colorName;

  // Remplacer "/" par " / " pour améliorer la lisibilité
  return colorName.replace(/\//g, ' / ');
}

/**
 * Normalise une chaîne pour la recherche (supprime accents, espaces, casse)
 * CENTRALISATION : Évite les duplications entre différentes pages
 */
export function normalizeForSearch(str = '') {
  if (!str || typeof str !== 'string') return '';

  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // supprime les accents
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Garde uniquement lettres et chiffres
    .trim();
}

